/**
 * Nom humain d'un document et nom de périmètre implicite (chantier C8, vague 3).
 *
 * Quand un lecteur ajoute un document depuis l'écran « Commencer », Anagogia
 * crée pour lui le périmètre qui l'accueillera : il s'appelle comme le
 * document, sans extension ni soulignés — le mot « périmètre » n'apparaît
 * nulle part sur ce chemin. Miroir client de `humanizeFileName`
 * (`api/src/utils/noteSourceLabel.ts`).
 */

import { isReservedDossierName } from './perimetreLabel';

const DOCUMENT_EXTENSION = /\.(pdf|epub|html?|docx?|txt|md|rtf|odt)$/i;

function flatten(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** « Saint-Martin_vie.pdf » → « Saint-Martin vie » ; un nom sans extension connue reste intact. */
export function documentTitleFromFileName(fileName: string): string {
  const trimmed = flatten(fileName);
  const withoutExtension = trimmed.replace(DOCUMENT_EXTENSION, '');
  return flatten(withoutExtension.replace(/_+/g, ' ')) || trimmed || 'Document';
}

/**
 * Nom de périmètre unique : le nom demandé, ou « nom (2) », « nom (3) »… si
 * l'utilisateur en a déjà un du même nom (comparaison insensible à la casse)
 * ou si le nom est réservé au dossier Général (`isReservedDossierName`) —
 * « Général.pdf » crée « Général (2) », jamais un second Général.
 */
export function uniquePerimetreName(base: string, existingNames: ReadonlyArray<string>): string {
  const taken = new Set(existingNames.map(n => n.trim().toLowerCase()));
  const isTaken = (name: string) => taken.has(name.toLowerCase()) || isReservedDossierName(name);
  const wanted = flatten(base) || 'Document';
  if (!isTaken(wanted)) return wanted;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${wanted} (${i})`;
    if (!isTaken(candidate)) return candidate;
  }
  return `${wanted} (${Date.now()})`;
}
